// src/pages/Terms.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function TermsPage() {
  const termsText = `GWIN TERMS OF USE
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Introduction
Welcome to Gwin.
Gwin is a gamified financial literacy and funding-readiness platform designed to help entrepreneurs improve their financial knowledge, financial habits, business discipline, and readiness for future growth opportunities.
These Terms of Use govern your access to and use of the Gwin website, web application, mobile application, content, templates, tools, assessments, scores, dashboards, and related services.
By creating an account, accessing Gwin, using the platform, completing lessons, submitting information, using templates, or making payment, you agree to be bound by these Terms.
If you do not agree to these Terms, you may not use Gwin.

2. Definitions
In these Terms:
“Gwin”, “we”, “us”, or “our” means Marion Rich Pty Ltd, trading as Gwin.
“User”, “you”, or “your” means any person who accesses or uses Gwin.
“Platform” means the Gwin website, application, dashboard, learning system, scoring system, templates, assessments, tools, and related services.
“Content” means all lessons, quizzes, scripts, text, images, videos, audio, templates, exercises, frameworks, prompts, guides, checklists, dashboards, scores, reports, badges, XP systems, unlocks, and any other material made available through Gwin.
“Score” means any score, rating, readiness indicator, progress indicator, credibility indicator, funding-readiness indicator, behavioural score, knowledge score, application score, or similar measurement generated by Gwin.
“Subscription” means any paid access plan, recurring payment plan, premium account, paid course access, cohort access, or paid feature offered by Gwin.
“Personal Information” has the meaning given to it under the Protection of Personal Information Act, 4 of 2013.

3. Eligibility to Use Gwin
You may only use Gwin if you:
are at least 18 years old;
have legal capacity to enter into binding agreements;
provide accurate and truthful information;
use the Platform for lawful business, educational, or personal development purposes;
comply with these Terms and all applicable South African laws.
If you use Gwin on behalf of a business, you confirm that you are authorised to do so.

4. Nature of the Gwin Service
Gwin provides general business finance education, practical learning tools, behaviour-building activities, progress tracking, and funding-readiness support.
Gwin may help users understand concepts such as:
business finance;
recordkeeping;
cash flow;
pricing;
compliance awareness;
financial discipline;
funding readiness;
business decision-making;
financial habits;
practical business templates.
Gwin is not a bank, lender, credit provider, financial adviser, tax practitioner, attorney, accountant, debt counsellor, or investment adviser.

5. No Financial, Legal, Tax, Accounting, Credit, Lending, or Investment Advice
Gwin provides general educational and informational content only.
Nothing on Gwin should be treated as:
financial advice;
legal advice;
tax advice;
accounting advice;
investment advice;
credit advice;
debt counselling;
lending advice;
funding approval guidance;
a recommendation to accept any financial product.
You remain responsible for your own business decisions.
You should consult a qualified professional before making important decisions relating to tax, accounting, law, credit, funding, debt, investment, or business finance.

6. No Guarantee of Funding
Gwin does not guarantee that you will receive funding, credit, loans, grants, investment, sponsorship, business opportunities, supplier opportunities, or any financial product.
Any funding-readiness score, readiness assessment, template, checklist, badge, certificate, progress result, dashboard indicator, or unlock is for educational and readiness-support purposes only.
A funder, bank, investor, lender, grant provider, or partner may still reject your application, even if you use Gwin or achieve a high score on the Platform.
Gwin does not control third-party funder decisions.

7. User Accounts
To use certain features, you may need to create an account.
You agree to:
provide accurate, current, and complete information;
keep your account information updated;
keep your password secure;
use your own account only;
notify us immediately if your account is compromised;
accept responsibility for all activity under your account.
You may not:
create an account using false information;
impersonate another person or business;
create multiple accounts to manipulate results;
sell, rent, transfer, or share your account;
allow another person to complete lessons, assessments, or submissions on your behalf;
use another person’s login details.

8. No Sharing of Login Credentials
Your login details are personal to you.
You may not share your username, password, access link, paid subscription, dashboard access, course access, or any other login credential with any other person.
If we reasonably suspect credential sharing, we may:
suspend your account;
limit your access;
terminate your account;
require identity verification;
refuse refunds;
take legal action where appropriate.

9. Acceptable Use
You may only use Gwin for lawful purposes and in accordance with these Terms.
You may not use Gwin to:
break any law;
commit fraud;
mislead Gwin, funders, partners, or other users;
submit false or manipulated information;
abuse, harass, threaten, or harm others;
upload malicious code;
interfere with the Platform;
copy, scrape, extract, or misuse data;
reverse engineer the Platform;
manipulate scores;
bypass payment systems;
access features you have not paid for;
use the Platform to build a competing product;
commercially exploit Gwin’s content, templates, or systems without written permission.

10. No Copying of Content
All Gwin Content belongs to Gwin or its licensors.
You may not copy, reproduce, adapt, translate, publish, distribute, sell, resell, upload, scrape, mirror, license, train AI systems on, or create derivative works from Gwin Content without our prior written consent.
This includes, but is not limited to:
lessons;
quizzes;
quiz logic;
answers;
explanations;
scripts;
voice-over text;
templates;
practical assignments;
scoring explanations;
score structures;
diagrams;
frameworks;
dashboards;
certificates;
reports;
user journeys;
XP systems;
unlock systems;
interface copy.
You may use Gwin Content for your own internal learning and business improvement only.

11. No Commercial Reuse of Templates
Gwin may provide templates, checklists, calculators, guides, worksheets, funding-readiness documents, financial routines, or other practical tools.
You may use these tools for your own business.
You may not:
sell them;
give them to clients as your own product;
include them in a paid course;
use them in workshops without permission;
upload them to template marketplaces;
reproduce them in books, guides, or online content;
give them to a competitor;
use them to build a competing platform;
remove Gwin branding or copyright notices;
commercially exploit them in any way without written permission.
Accountants, bookkeepers, coaches, consultants, incubators, ESD providers, or advisors who wish to use Gwin templates with clients must enter into a separate partner agreement with Gwin.

12. No Scraping, Data Extraction, or Automated Access
You may not use bots, crawlers, scrapers, scripts, browser extensions, automated tools, data-mining systems, AI agents, or similar technologies to access, copy, download, monitor, extract, index, or harvest any part of Gwin.
You may not:
scrape lessons;
scrape quizzes;
extract user data;
extract scoring logic;
copy platform workflows;
build datasets from Gwin Content;
use Gwin Content to train AI models;
bypass rate limits or access controls;
perform automated testing without permission.
We may block, throttle, suspend, or terminate accounts that appear to be using automated access.

13. No Reverse Engineering
You may not reverse engineer, decompile, disassemble, inspect, analyse, derive, copy, or attempt to discover the source code, scoring logic, algorithms, data models, ranking rules, unlock logic, security systems, or technical architecture of Gwin.
You may not attempt to recreate Gwin’s:
scoring system;
funding-readiness logic;
behavioural assessment model;
lesson progression system;
gamification structure;
data architecture;
dashboard logic;
partner or funder matching logic;
technical workflows.

14. False Submissions
You agree that all information you submit to Gwin must be truthful, accurate, and complete.
You may not submit false, incomplete, misleading, manipulated, or fraudulent information, including false information about:
your identity;
your business;
your revenue;
your expenses;
your compliance status;
your documents;
your quiz answers;
your activity completion;
your financial behaviour;
your funding readiness;
your business stage;
your bank or accounting information;
any information submitted for scoring or assessment.
If you submit false information, we may suspend or terminate your account and invalidate any scores, certificates, reports, or readiness indicators.

15. No Gaming of Scores
Gwin may generate scores, progress indicators, readiness indicators, badges, certificates, rankings, reports, or unlocks.
You may not manipulate, artificially inflate, or game any score or progress indicator.
Prohibited conduct includes:
submitting false information;
completing activities dishonestly;
asking another person to complete activities for you;
creating multiple accounts;
repeatedly retaking activities in an abusive manner;
exploiting bugs;
manipulating data inputs;
bypassing required tasks;
using scripts or automation;
providing false evidence of practical work;
misrepresenting your score to funders or partners.
Gwin may review, adjust, withhold, suspend, reset, or remove any score, badge, certificate, unlock, or readiness result if we suspect manipulation, error, abuse, or dishonesty.

16. Scores Are Not Credit Scores or Funding Decisions
Any Gwin score is an internal educational, behavioural, readiness, or progress indicator.
A Gwin score is not:
a credit score;
a credit bureau report;
a loan approval;
a guarantee of funding;
an investment rating;
a financial statement audit;
a professional certification;
a legal compliance confirmation;
a tax clearance confirmation;
a binding assessment by any funder.
You may not present a Gwin score as an official credit score, funding approval, or professional financial certification unless Gwin has expressly authorised that use in writing.

17. User Content and Submissions
You may submit information, documents, answers, comments, data, reflections, templates, financial information, business information, or other materials to Gwin.
You retain ownership of your own original business information.
However, you grant Gwin a limited licence to use your submissions for purposes of:
providing the Platform;
generating scores;
tracking your progress;
personalising your learning journey;
improving the Platform;
providing support;
creating aggregated and anonymised insights;
complying with law;
producing reports where you have consented or where data is anonymised.
We will process Personal Information in accordance with our Privacy Policy and applicable law.

18. Privacy and Personal Information
Gwin processes Personal Information in accordance with POPIA and our Privacy Policy.
By using Gwin, you agree that we may process your Personal Information for lawful platform-related purposes, including:
account creation;
identity and business profile management;
learning progress tracking;
score generation;
subscription and payment administration;
customer support;
platform improvement;
security and fraud prevention;
legal compliance;
communication with you.
Where required, we will ask for separate consent before sharing your personal, business, score, funding-readiness, credit-related, bank, accounting, or financial information with third parties.

19. Third-Party Services
Gwin may integrate with or link to third-party services, including:
payment providers;
cloud hosting providers;
analytics providers;
accounting software;
banks;
funders;
credit bureaus;
identity verification providers;
CRM tools;
email or WhatsApp communication tools;
partner platforms.
Third-party services are governed by their own terms and privacy policies.
Gwin is not responsible for third-party decisions, failures, fees, downtime, delays, approvals, rejections, products, services, or advice.

20. Payment and Subscription Terms
Some Gwin features may require payment.
By subscribing or purchasing paid access, you agree to:
pay all applicable fees;
provide accurate billing information;
authorise recurring payments where applicable;
keep your payment method updated;
pay any taxes, charges, or transaction fees where applicable.
Subscription fees, billing cycles, features, and pricing will be shown before payment.
Unless stated otherwise, subscriptions renew automatically until cancelled.

21. Subscription Cancellation
You may cancel your subscription through your account settings or by contacting Gwin at [insert support email].
Cancellation will stop future billing, but it does not automatically entitle you to a refund for amounts already paid, except where required by law or expressly stated in these Terms.
If you cancel during a billing period, you may continue to access paid features until the end of that billing period, unless your account has been suspended or terminated for breach.

22. Refund Policy
Subject to applicable South African law, Gwin’s refund policy is as follows:
Monthly subscriptions are generally non-refundable once the billing period has started.
If you cancel, your cancellation applies to the next billing cycle.
We may provide refunds at our discretion where there has been a billing error, duplicate payment, technical failure caused by Gwin, or another valid reason accepted by us.
We do not provide refunds where your account is suspended or terminated because you breached these Terms.
We do not provide refunds because you failed to use the Platform after paying.
We do not provide refunds because you did not receive funding, business improvement, a score increase, or any expected commercial outcome.
We do not provide refunds for dissatisfaction caused by your own misunderstanding of the Platform where the Platform was accurately described.
Where the Electronic Communications and Transactions Act or other applicable law gives you a statutory cooling-off or cancellation right, Gwin will comply with that law. The ECTA provides consumer protection rules for electronic transactions, including cooling-off rights for certain online services.
To request a refund, contact [insert support email] with your name, account email, payment date, amount paid, and reason for the request.

23. Price Changes
Gwin may change its pricing, subscription plans, or paid features from time to time.
Where required, we will give reasonable notice of material price changes affecting existing subscribers.
If you do not agree to a price change, you may cancel your subscription before the new price applies.

24. Free Trials, Promotions, and Discounts
Gwin may offer free trials, discounts, sponsored access, coupon codes, pilot access, or promotional pricing.
These offers may be subject to additional terms.
Gwin may withdraw, amend, or limit promotional offers at any time.
If a free trial converts into a paid subscription, we will disclose this before payment is taken.

25. Intellectual Property
All rights in Gwin and its Content belong to Gwin or its licensors.
This includes all:
trademarks;
logos;
mascot designs;
brand names;
lesson content;
scripts;
quizzes;
templates;
frameworks;
dashboards;
scoring systems;
data models;
software;
source code;
workflows;
designs;
graphics;
reports;
certificates;
databases;
business methods;
trade secrets;
know-how.
You receive a limited, revocable, non-exclusive, non-transferable licence to use Gwin for your own lawful internal business learning and development purposes.
You do not acquire ownership of Gwin, its Content, or any related intellectual property.

26. Trademarks
The name “Gwin”, the Gwin logo, mascot, slogans, badges, product names, and related brand elements may not be used without our prior written permission.
You may not use Gwin branding in a way that suggests partnership, endorsement, certification, accreditation, funding approval, or official affiliation unless we have agreed to it in writing.

27. Platform Availability
We aim to keep Gwin available and functioning, but we do not guarantee uninterrupted or error-free access.
The Platform may be unavailable due to:
maintenance;
updates;
hosting issues;
internet failures;
third-party service failures;
cyber incidents;
force majeure events;
technical defects;
changes to the Platform.
We are not liable for loss caused by downtime, data delays, errors, interruptions, or unavailability, except where liability cannot legally be excluded.

28. Changes to the Platform
Gwin may change, improve, remove, suspend, or discontinue any part of the Platform at any time.
This includes changes to:
lessons;
features;
templates;
scores;
scoring logic;
dashboards;
subscriptions;
unlocks;
partner integrations;
reports;
pricing;
user journeys.
We may make these changes to improve the Platform, protect users, comply with law, prevent abuse, or support business sustainability.

29. Suspension and Termination Rights
We may suspend, restrict, or terminate your account immediately if we reasonably believe that you have:
breached these Terms;
shared login credentials;
copied or misused Gwin Content;
scraped or extracted data;
reverse engineered the Platform;
submitted false information;
gamed or manipulated scores;
failed to pay fees;
used the Platform unlawfully;
interfered with Platform security;
abused staff, users, or partners;
infringed intellectual property rights;
created risk for Gwin, users, partners, or funders;
used Gwin to build or support a competing product.
We may also suspend access where required by law, security concerns, payment disputes, partner requirements, fraud prevention, or platform integrity.
Termination does not affect any rights or obligations that arose before termination.

30. Consequences of Termination
If your account is terminated:
your access to the Platform may end immediately;
your scores, certificates, unlocks, reports, or progress may be removed or invalidated;
you may lose access to paid features;
you may not create a new account without permission;
you must stop using Gwin Content;
you remain liable for unpaid fees;
clauses relating to IP, confidentiality, restrictions, liability, dispute resolution, and governing law remain in force.

31. User Responsibility for Business Decisions
You are solely responsible for all decisions you make based on Gwin Content, including decisions about:
pricing;
cash flow;
tax;
compliance;
debt;
funding;
hiring;
spending;
accounting;
business strategy;
investment;
applying for finance.
Gwin does not guarantee that using the Platform will improve your business, increase revenue, reduce expenses, improve compliance, secure funding, or prevent business failure.

32. Limitation of Liability
To the maximum extent allowed by South African law, Gwin will not be liable for any indirect, incidental, special, consequential, punitive, or economic loss, including:
loss of profit;
loss of revenue;
loss of business;
loss of goodwill;
loss of opportunity;
loss of funding;
rejection by a funder;
inaccurate user submissions;
poor business decisions;
tax or compliance penalties;
reliance on educational content;
platform downtime;
data loss;
third-party service failures;
unauthorised account access caused by your failure to secure your login details.
Where liability cannot legally be excluded, our liability will be limited to the amount you paid to Gwin in the three months before the claim arose, unless applicable law requires otherwise.
Nothing in these Terms excludes liability that cannot be excluded under South African law.

33. No Warranties
Gwin is provided on an “as is” and “as available” basis.
We do not warrant that:
the Platform will always be available;
the Platform will be error-free;
all content will be complete or current;
your score will be accurate in all circumstances;
your business will improve;
you will receive funding;
funders will accept your information;
templates will meet every business need;
the Platform will meet your expectations;
the Platform will be compatible with every device or system.

34. Indemnity
You agree to indemnify and hold Gwin harmless from any claim, loss, damage, liability, penalty, cost, or expense arising from:
your breach of these Terms;
your misuse of the Platform;
false information submitted by you;
score manipulation;
unauthorised sharing of content;
infringement of intellectual property;
unlawful conduct;
your reliance on Gwin as professional advice;
your dealings with third parties;
your breach of any law or third-party rights.

35. Communications
We may communicate with you by email, in-app notification, WhatsApp, SMS, phone, or other electronic channels where permitted.
Platform-related communications may include:
account notices;
payment notices;
security alerts;
subscription notices;
support messages;
legal updates;
service announcements.
Marketing communications will be handled in accordance with applicable law and your communication preferences.

36. Feedback and Suggestions
If you give Gwin feedback, ideas, suggestions, feature requests, or improvement recommendations, you agree that Gwin may use them without owing you payment, ownership, royalties, or compensation.
You may not submit ideas that belong to someone else or are confidential to a third party.

37. Confidential Platform Information
Certain aspects of Gwin are confidential and proprietary, including:
scoring logic;
readiness logic;
data models;
funding-readiness methodology;
partner logic;
product roadmap;
internal reports;
non-public platform features;
commercial strategy;
technical architecture.
You may not disclose, copy, publish, reverse engineer, or use confidential Gwin information for any purpose other than using the Platform as allowed by these Terms.

38. Beta Features and Pilot Programmes
Gwin may offer beta, pilot, experimental, or early-access features.
These features may be incomplete, unstable, inaccurate, or changed without notice.
We may withdraw beta or pilot access at any time.
You use beta features at your own risk.

39. Partner, Advisor, and Institutional Access
If you access Gwin through an accountant, bookkeeper, business coach, incubator, accelerator, ESD programme, sponsor, employer, funder, or institutional partner, additional terms may apply.
Depending on your consent and the relevant programme rules, selected progress data, attendance data, completion data, or aggregated reporting may be shared with the relevant partner.
We will not share your personal funding-readiness, score, financial, credit, bank, or accounting information with a third party unless legally permitted and, where required, you have consented.

40. Complaints and Support
If you have a complaint or support query, contact us at:
Email: admin@gwin.co.za
Business address: 22 Percy Road, Ottery, Cape Town
Company: Marion Rich Pty Ltd t/a Gwin
We will aim to respond within a reasonable time.

41. Dispute Resolution
If a dispute arises, you agree to first contact us and attempt to resolve the dispute informally.
If the dispute cannot be resolved informally within 14 days, either party may refer the dispute to mediation, unless urgent legal relief is required.
If mediation fails, either party may approach a court of competent jurisdiction in South Africa.
Nothing in these Terms prevents either party from seeking urgent interdictory or injunctive relief for intellectual property infringement, confidentiality breaches, data misuse, unlawful access, or platform abuse.

42. Governing Law and Jurisdiction
These Terms are governed by the laws of the Republic of South Africa.
You consent to the jurisdiction of the courts of South Africa for disputes arising from or relating to these Terms or your use of Gwin.

43. Changes to These Terms
We may update these Terms from time to time.
If we make material changes, we will take reasonable steps to notify users.
Your continued use of Gwin after changes become effective means you accept the updated Terms.
If you do not agree with the updated Terms, you must stop using Gwin and cancel your subscription where applicable.

44. Severability
If any part of these Terms is found to be invalid, unlawful, or unenforceable, the remaining parts will continue to apply.

45. Entire Agreement
These Terms, together with the Privacy Policy and any applicable subscription, partner, pilot, or programme terms, form the entire agreement between you and Gwin regarding your use of the Platform.

46. Contact Details
For legal notices, support, or account queries, contact:
Marion Rich Pty Ltd t/a Gwin
Registration number: 2022 / 577834 / 07 
Email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za`;

  return (
    <LegalPageLayout title="Terms of Use">
      <LegalDocumentContent documentText={termsText} />
    </LegalPageLayout>
  );
}